<script>
import { headerMenuProps, useHeaderMenu } from "./mixin";
import List from "@/components/molecules/list";
import LazyImage from "@/components/atoms/lazy-image";
import Button from "@/components/atoms/button";
import MenuLayer from "@/components/organisms/header/menu/menu-layer/default";
import ListItem from "@/components/molecules/list/list-item";
import Icon from "@/components/atoms/icon";
import ButtonIcon from "@/components/molecules/button-icon";
import MenuSeo from "@/containers/menu/menu-seo";
import coraproLogo from "@/assets/images/corapro-logo.svg";

export default {
  name: "HeaderMenu",
  components: {
    List,
    LazyImage,
    Button,
    MenuLayer,
    ListItem,
    Icon,
    ButtonIcon,
    MenuSeo
  },
  props: {
    miamPushZone: {
      type: Array,
      required: false,
      default: () => []
    },
    ...headerMenuProps()
  },
  emits: ["shown", "hidden"],
  setup(props) {
    const { menuCodes, categoriesWithoutHeader } = useHeaderMenu(props);
    return { menuCodes, coraproLogo, categoriesWithoutHeader };
  },
  data() {
    return {
      cancelHide: false,
      currentCategory: null,
      isShowLayer: false,
      hoverTimer: null,
      miamCategory: {
        id: "C-MIAM",
        attributes: {
          libelle: "Nos recettes et idées repas",
          isMiam: true,
          children: [],
          push_zones: []
        }
      }
    };
  },
  methods: {
    hideFromFirstLevel(category) {
      this.currentCategory = category;
      this.hide();
    },
    hide() {
      this.hideLayer();
    },
    showLayer(category) {
      this.currentCategory = category;
      this.isShowLayer = true;
      this.$emit("shown");
    },
    hideLayer() {
      this.isShowLayer = false;
      if (this.$refs.layer) {
        this.$refs.layer.reset();
      }
      this.$emit("hidden");
    },
    openOnMouseEnter(category) {
      this.currentCategory = category;
      this.clearHoverTimer();
      this.hoverTimer = setTimeout(() => {
        this.showLayer(this.currentCategory);
      }, 200);
    },
    closeOnMouseLeave() {
      this.clearHoverTimer();
      this.cancelHide = false;
      this.hoverTimer = setTimeout(() => {
        !this.cancelHide && this.hideLayer();
        this.cancelHide = false;
      }, 500);
    },
    clearHoverTimer() {
      clearTimeout(this.hoverTimer);
      this.hoverTimer = null;
    },
    blockAccessToUniverse(evt) {
      evt.stopPropagation();
      evt.preventDefault();
      return false;
    },
    cancelTheHideAction() {
      this.cancelHide = true;
    },
    handleSpace() {
      if (
        this.currentCategory.attributes.children &&
        this.currentCategory.attributes.children.data.length > 0
      ) {
        this.showLayer(this.currentCategory);
      } else {
        navigateToInternal(this.currentCategory.attributes.slug);
      }
    }
  }
};
</script>

<template>
  <div class="c-header-menu">
    <MenuSeo v-if="isNational && isHomePage" />
    <nav
      class="c-header-menu__nav"
      role="navigation"
      @mouseleave="closeOnMouseLeave()"
      @mouseenter="cancelTheHideAction()"
      @keyup.esc="hideLayer()"
      @keypress.space.prevent="handleSpace()"
    >
      <List class="c-header-menu__nav-list">
        <template
          v-for="(category, index) in categoriesWithoutHeader"
          :key="category.id"
        >
          <template v-if="!category.attributes.hidden_on_desktop">
            <ListItem
              class="c-header-menu__nav-list-item"
              :class="
                currentCategory &&
                  currentCategory.id === category.id &&
                  isShowLayer &&
                  'c-header-menu__nav-list-item--active'
              "
            >
              <template v-if="category.attributes.children && category.attributes.children.data.length > 0">
                <!-- Volontairement href et non to car impossible de faire un preventDefault sur un nuxt-link -->
                <Button
                  shadow="lg"
                  size="md"
                  :variant="index === 0 ? 'primary' : null"
                  menu-item
                  :class="[
                    index === 0
                      ? 'c-header-menu__nav-button-hightlight'
                      : 'c-header-menu__nav-button',
                    category.attributes.colors &&
                      'c-header-menu__nav-button-with-color'
                  ]"
                  class="c-menu__button"
                  :href="category.attributes.slug"
                  role="button"
                  :style="[
                    category.attributes.colors && {
                      '--boutique-bg-color-hex': `${category.attributes.colors.background}`,
                      '--boutique-bg-hover-color-hex': `${category.attributes.colors.background_hover}`,
                      '--boutique-text-color-hex': `${category.attributes.colors.text}`,
                      '--boutique-text-hover-color-hex': `${category.attributes.colors.text_hover}`
                    }
                  ]"
                  :aria-expanded="
                    isShowLayer && currentCategory === category ? 'true' : 'false'
                  "
                  @mouseenter="openOnMouseEnter(category)"
                  @focus="showLayer(category)"
                  @click.prevent="blockAccessToUniverse"
                >
                  <Icon
                    v-if="index === 0"
                    name="basket"
                    variant="white"
                  />
                  <Icon
                    v-if="category.id === boutiqueExcluWebId"
                    name="exclu-web"
                    variant="black"
                    size="sm"
                    class="c-header-menu__icon u-none@md u-mr-xxs"
                  />
                  {{ category.attributes.libelle }}
                  <span
                    v-if="
                      category.attributes.colors &&
                        category.attributes.colors.background
                    "
                    :style="
                      category.attributes.colors.background && {
                        backgroundColor: `${category.attributes.colors.background}`
                      }
                    "
                    class="c-header-menu__nav-button-line"
                  >
                  </span>
                  <div
                    class="c-header-menu-mask"
                    @click.stop.prevent
                  ></div>
                </Button>
              </template>
              <!-- dans le cas ou on devrait faire une redirection et on a pas besoin d'afficher une popin-anchor -->
              <Button
                v-else
                ref="button"
                size="md"
                class="c-header-menu__nav-button"
                :class="[
                  category.attributes.colors &&
                    'c-header-menu__nav-button-with-color'
                ]"
                :to="category.attributes.slug"
                :style="[
                  category.attributes.colors && {
                    '--boutique-bg-color-hex': `${category.attributes.colors.background}`,
                    '--boutique-bg-hover-color-hex': `${category.attributes.colors.background_hover}`,
                    '--boutique-text-color-hex': `${category.attributes.colors.text}`,
                    '--boutique-text-hover-color-hex': `${category.attributes.colors.text_hover}`
                  }
                ]"
                @mouseenter="hideFromFirstLevel(category)"
                @focus="hideFromFirstLevel(category)"
              >
                {{ category.attributes.libelle }}
                <span
                  v-if="
                    category.attributes.colors &&
                      category.attributes.colors.background
                  "
                  :style="
                    category.attributes.colors.background && {
                      backgroundColor: `${category.attributes.colors.background}`
                    }
                  "
                  class="c-header-menu__nav-button-line"
                >
                </span>
              </Button>
            </ListItem>
            <client-only
              v-if="index === 0"
              :key="`miam-${index}`"
            >
              <ListItem
                v-if="showMesRepas"
                id="miamMesRepas"
                class="c-header-menu__nav-list-item"
                style="display: none;"
              >
                <Button
                  ref="button"
                  size="md"
                  class="c-header-menu__nav-button"
                  @mouseenter="openOnMouseEnter(miamCategory)"
                  @focus="showLayer(miamCategory)"
                  @click.prevent="blockAccessToUniverse"
                >
                  Nos recettes et idées repas
                </Button>
              </ListItem>
            </client-only>
          </template>
        </template>
      </List>
      <div
        v-if="currentCategory && currentCategory.attributes.children"
        v-show="isShowLayer"
        class="c-header-menu__layer u-box-shadow-lg"
      >
        <div class="c-header-menu__layer-close">
          <ButtonIcon
            class="c-popin-anchor__close"
            icon-color="text-light"
            icon-color-hover="primary-light"
            size="sm"
            icon-size="sm"
            icon-name="cross"
            aria-label="Fermer le menu"
            @click="hideLayer()"
          />
        </div>
        <MenuLayer
          ref="layer"
          :show-links="!isShowLayer"
          :category="currentCategory"
          :brands="currentCategory.attributes.marques"
          :miam-push-zone="miamPushZone"
        />
      </div>

      <div class="c-header-menu__layer-service">
        <Button
          class="c-header-menu__corapro-button"
          to="/infos-et-services/les-magasins-cora/profitez-des-services/cora-pro"
        >
          <LazyImage
            :src="coraproLogo"
            alt="corapro"
            width="62"
          />
        </Button>
        <Button
          class="c-header-menu__layer-service-button"
          to="/infos-et-services"
        >
          Services & infos magasins
        </Button>
      </div>
    </nav>
  </div>
</template>
<style lang="scss" scoped>
@import "@/assets/styles/components/header-menu.scss";
</style>
